import React, { useState, useEffect } from 'react';
import { ConfigEditor } from './ConfigEditor';
import { ConfigPresets } from './ConfigPresets';
import { 
  MCPConfigTemplate, 
  GeneratorOptions, 
  ConfigValidationResult, 
  ConnectionPlan,
  Connection,
  PlanApp 
} from '../../types';
import { configGenerator } from '../../services/configGenerator';
import { useNotifications } from '../providers/NotificationProvider';

interface ConfigGeneratorProps {
  plan?: ConnectionPlan;
  connections?: Connection[];
  onSave?: (config: MCPConfigTemplate, options: GeneratorOptions) => void;
  className?: string;
}

export function ConfigGenerator({ plan, connections = [], onSave, className }: ConfigGeneratorProps) {
  const [options, setOptions] = useState<GeneratorOptions>({
    templateType: 'mixed',
    baseUrl: 'http://localhost:3010',
    includeEnv: true,
    outputFormat: 'json',
    clientType: 'claude-desktop',
  });

  const [generatedConfig, setGeneratedConfig] = useState<MCPConfigTemplate | null>(null);
  const [validation, setValidation] = useState<ConfigValidationResult>({
    isValid: true,
    errors: [],
    warnings: [],
    suggestions: [],
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const { addNotification } = useNotifications();

  // Generate configuration when plan or options change
  useEffect(() => {
    if (plan && plan.apps.length > 0) {
      generateConfiguration();
    }
  }, [plan, options]);

  const generateConfiguration = async () => {
    if (!plan || plan.apps.length === 0) {
      setGeneratedConfig(null);
      return;
    }

    setIsGenerating(true);
    try {
      const config = configGenerator.generateConfig(plan.apps, connections, options);
      setGeneratedConfig(config);
      
      // Validate the generated config
      const validationResult = configGenerator.validateConfig(config);
      setValidation(validationResult);
      
      if (!validationResult.isValid) {
        addNotification({
          type: 'warning',
          title: 'Configuration Issues',
          message: `Generated configuration has ${validationResult.errors.length} errors and ${validationResult.warnings.length} warnings`,
        });
      }
    } catch (error) {
      console.error('Failed to generate configuration:', error);
      addNotification({
        type: 'error',
        title: 'Generation Failed',
        message: error instanceof Error ? error.message : 'Failed to generate configuration',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOptionChange = <K extends keyof GeneratorOptions>(
    key: K,
    value: GeneratorOptions[K]
  ) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  const handlePresetSelect = (preset: Partial<GeneratorOptions>) => {
    setOptions(prev => ({ ...prev, ...preset }));
    setShowPresets(false);
  };

  const handleSaveConfig = () => {
    if (generatedConfig && validation.isValid) {
      onSave?.(generatedConfig, options);
      addNotification({
        type: 'success',
        title: 'Configuration Saved',
        message: 'MCP configuration has been saved successfully',
      });
    }
  };

  const downloadConfig = () => {
    if (!generatedConfig) return;

    const content = options.outputFormat === 'yaml' 
      ? configGenerator.toYAML(generatedConfig)
      : configGenerator.toJSON(generatedConfig, true);
    
    const filename = `mcp-config.${options.outputFormat}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    addNotification({
      type: 'success',
      title: 'Configuration Downloaded',
      message: `Configuration saved as ${filename}`,
    });
  };

  const copyToClipboard = async () => {
    if (!generatedConfig) return;

    const content = options.outputFormat === 'yaml' 
      ? configGenerator.toYAML(generatedConfig)
      : configGenerator.toJSON(generatedConfig, true);

    try {
      await navigator.clipboard.writeText(content);
      addNotification({
        type: 'success',
        title: 'Copied to Clipboard',
        message: 'Configuration copied to clipboard',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Copy Failed',
        message: 'Failed to copy configuration to clipboard',
      });
    }
  };

  return (
    <div className={`space-y-4 sm:space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            MCP Configuration Generator
          </h2>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Generate MCP server configurations from your connection plan
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowPresets(!showPresets)}
            className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            Presets
          </button>
          {generatedConfig && validation.isValid && (
            <>
              <button
                onClick={copyToClipboard}
                className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Copy
              </button>
              <button
                onClick={downloadConfig}
                className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                Download
              </button>
              {onSave && (
                <button
                  onClick={handleSaveConfig}
                  className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                >
                  Save
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Presets Panel */}
      {showPresets && (
        <ConfigPresets
          onSelect={handlePresetSelect}
          onClose={() => setShowPresets(false)}
        />
      )}

      {/* Configuration Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        {/* Template Type */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Template Type
          </label>
          <select
            value={options.templateType}
            onChange={(e) => handleOptionChange('templateType', e.target.value as GeneratorOptions['templateType'])}
            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="dedicated">Dedicated Servers</option>
            <option value="remote">Remote Endpoints</option>
            <option value="mixed">Mixed Mode</option>
          </select>
        </div>

        {/* Client Type */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Client Type
          </label>
          <select
            value={options.clientType}
            onChange={(e) => handleOptionChange('clientType', e.target.value as GeneratorOptions['clientType'])}
            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="claude-desktop">Claude Desktop</option>
            <option value="cursor">Cursor</option>
            <option value="zed">Zed</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        {/* Output Format */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Output Format
          </label>
          <select
            value={options.outputFormat}
            onChange={(e) => handleOptionChange('outputFormat', e.target.value as GeneratorOptions['outputFormat'])}
            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="json">JSON</option>
            <option value="yaml">YAML</option>
          </select>
        </div>

        {/* Base URL */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
            Base URL
          </label>
          <input
            type="url"
            value={options.baseUrl || ''}
            onChange={(e) => handleOptionChange('baseUrl', e.target.value)}
            placeholder="http://localhost:3010"
            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Include Environment Variables */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="includeEnv"
          checked={options.includeEnv}
          onChange={(e) => handleOptionChange('includeEnv', e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
        />
        <label htmlFor="includeEnv" className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
          Include environment variables in configuration
        </label>
      </div>

      {/* Plan Summary */}
      {plan && (
        <div className="p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="text-xs sm:text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
            Plan Summary
          </h3>
          <div className="text-xs sm:text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <p>• {plan.apps.length} apps configured</p>
            <p>• {connections.length} connections available</p>
            <p>• Template: {options.templateType}</p>
            <p>• Target: {options.clientType}</p>
          </div>
        </div>
      )}

      {/* Generation Status */}
      {isGenerating && (
        <div className="flex items-center justify-center p-8">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-600 dark:text-gray-400">Generating configuration...</span>
          </div>
        </div>
      )}

      {/* Configuration Editor */}
      {generatedConfig && !isGenerating && (
        <ConfigEditor
          initialConfig={generatedConfig}
          options={options}
          onConfigChange={setGeneratedConfig}
          onValidationChange={setValidation}
          height="500px"
        />
      )}

      {/* No Plan Message */}
      {!plan && !isGenerating && (
        <div className="text-center p-8 text-gray-500 dark:text-gray-400">
          <p>Create a connection plan to generate MCP configuration</p>
        </div>
      )}
    </div>
  );
}